import { CommonModule } from '@angular/common';
import { Component, inject, NgZone, OnDestroy } from '@angular/core';
import { BasePaymentComponent, LoggingService } from '@fgb/core';
import { FormsModule } from '@angular/forms';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as braintreeClient from 'braintree-web/client';
import * as braintreeHostedFields from 'braintree-web/hosted-fields';
import { HostedFields, HostedFieldsEvent } from 'braintree-web/hosted-fields';

/** Fields rendered as Braintree Hosted Fields, keyed to the div ids they mount into. */
type BraintreeFieldKey = 'number' | 'expirationDate' | 'cvv' | 'postalCode';

/**
 * Braintree Hosted Fields payment template (card number, expiry, CVV, zip). Registered under
 * ProviderName.BrainTree in PAYMENT_TEMPLATES so PaymentService.process() renders it for a
 * RenderSpecificIntegration response from that provider — the "normal" (non-express) card-entry flow.
 */
@Component({
  selector: 'fgbcl-payment-braintree',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingModule, FGBSharedModule],
  templateUrl: './payment-braintree.component.html',
  styleUrl: './payment-braintree.component.scss',
})
export class PaymentBraintreeComponent extends BasePaymentComponent implements OnDestroy {
  private hostedFields: HostedFields | null = null;
  private zone = inject(NgZone);
  private router = inject(Router);
  private modal = inject(NgbModal);
  private braintreeLogService = inject(LoggingService);
  errors: Partial<Record<BraintreeFieldKey, string>> = {};
  isLoading = false;
  hasError = false;

  /**
   * Whether the hosted field iframes have been created and can be tokenized. The submit button stays
   * disabled until this flips, otherwise a failed init leaves an enabled button whose click is
   * swallowed by the `hostedFields` guard in submit() with no feedback to the customer.
   */
  fieldsReady = false;

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnDestroy(): void {
    this.hostedFields?.teardown().catch(() => {});
    // Drop the reference so a re-opened modal re-creates the fields instead of reusing a torn-down
    // instance (submit() guards on hostedFields being present).
    this.hostedFields = null;
    this.fieldsReady = false;
    super.ngOnDestroy();
  }

  /* ================================
   * Public API
   * ================================ */

  init(): void {
    const authorization = this.properties?.clientToken;
    if (!authorization) {
      this.logError('Braintree: no client token available');
      return;
    }

    this.zone.runOutsideAngular(async () => {
      try {
        const client = await braintreeClient.create({ authorization });
        const hostedFields = await braintreeHostedFields.create({
          client,
          // Only the properties on Braintree's allowlist cross into the field iframes; anything else
          // (borders, background, radius) belongs on the container in the scss. Keep these in step
          // with .bt-field__input by hand — the stylesheet cannot reach inside a cross-origin frame.
          styles: {
            input: {
              'font-family': "'Inter', 'Open Sans', Arial, sans-serif",
              'font-size': '14px',
              'font-weight': '400',
              color: '#000',
            },
            ':focus': {
              color: '#000',
            },
            '::placeholder': {
              color: '#a5a5a5',
            },
            '.invalid': {
              color: '#dc3545',
            },
          },
          fields: {
            // Each field has a visible label above it, so only give a placeholder where the
            // expected format is not obvious from the label alone.
            number: { container: '#braintree-card-number' },
            expirationDate: { container: '#braintree-card-expiry', placeholder: 'MM/YY' },
            cvv: { container: '#braintree-card-cvv' },
            postalCode: { container: '#braintree-card-postal-code' },
          },
        });

        hostedFields.on('validityChange', (event) => this.onValidityChange(event));
        hostedFields.on('blur', (event) => this.onBlur(event));

        // Re-enter the zone: the create() continuation runs outside Angular, so the view would not
        // pick up fieldsReady and the submit button would stay disabled.
        this.zone.run(() => {
          this.hostedFields = hostedFields;
          this.fieldsReady = true;
        });
      } catch (error) {
        this.zone.run(() => this.logError('Braintree hosted fields failed to initialise', error));
      }
    });
  }

  submitForm(event: Event): void {
    event.preventDefault();
    this.submit();
  }

  submit(): void {
    if (this.isLoading || !this.hostedFields) {
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errors = {};

    // Every continuation below has to re-enter the zone — see requestPayment() for why.
    this.hostedFields
      .tokenize()
      .then((payload) => this.requestPayment(payload.nonce))
      .catch((error: any) =>
        this.zone.run(() => {
          this.hasError = true;
          this.assignFieldErrors(error);
        })
      )
      .finally(() =>
        this.zone.run(() => {
          this.isLoading = false;
        })
      );
  }

  /* ================================
   * Field event handlers
   * ================================ */

  /**
   * Live validation. `isPotentiallyValid` stays true while an entry could still become valid, so it
   * is the right signal for when to show an error — gating on `!isValid` would flash red on every
   * partially typed card number.
   */
  private onValidityChange(event: HostedFieldsEvent): void {
    const key = event.emittedBy as BraintreeFieldKey;
    const field = event.fields[event.emittedBy];

    this.zone.run(() => {
      if (field.isValid || field.isEmpty || field.isPotentiallyValid) {
        this.clearFieldError(key);
      } else {
        this.errors[key] = 'This field is invalid.';
      }
    });
  }

  /**
   * A half-typed entry stays potentially valid, so validityChange never flags it. Catch it on blur
   * instead: leaving a non-empty field that is not yet valid is a genuine error.
   */
  private onBlur(event: HostedFieldsEvent): void {
    const key = event.emittedBy as BraintreeFieldKey;
    const field = event.fields[event.emittedBy];

    if (field.isEmpty || field.isValid) {
      return;
    }

    this.zone.run(() => {
      this.errors[key] = 'This field is invalid.';
    });
  }

  /* ================================
   * Private Helpers
   * ================================ */

  /** Drop a field's error, and the form-level alert too once nothing is left flagged. */
  private clearFieldError(key: BraintreeFieldKey): void {
    delete this.errors[key];
    if (!Object.keys(this.errors).length) {
      this.hasError = false;
    }
  }

  private requestPayment(nonce: string): void {
    if (!nonce) {
      return;
    }

    // braintree-web bundles its own promise implementation, which Zone.js does not patch, so the
    // tokenize() continuation can land outside Angular. Re-enter the zone or the navigation runs
    // without change detection and the callback page never renders.
    this.zone.run(() => {
      const callbackUrl = this.context.callbackUrl;
      const params = { orderId: this.orderId, sourcekey: nonce };

      this.router.navigate([callbackUrl], { queryParams: params });

      this.modal.dismissAll();
      this.paymentService.destroyPaymentComponent();
    });
  }

  /** HOSTED_FIELDS_FIELDS_INVALID carries the offending field keys; surface a generic message per field. */
  private assignFieldErrors(error: any): void {
    const invalidFieldKeys: string[] = error?.details?.invalidFieldKeys ?? [];
    invalidFieldKeys.forEach((key) => {
      this.errors[key as BraintreeFieldKey] = 'This field is invalid.';
    });
  }

  private logError(message: string, error?: unknown): void {
    this.hasError = true;
    this.braintreeLogService.error(message, error);
  }
}
